import { useEffect, useState } from "react";
import { formatRelativeTime } from "../utils/time";

const REFRESH_MS = 30 * 60 * 1000; // the server-side cache (x-mentions.ts) only turns over every 18h, so this is just how often an open tab re-checks it
const TICK_MS = 5000; // re-render "X ago" labels without waiting on a full refetch
const SECONDS_PER_ITEM = 4;

interface XMention {
  id: string;
  text: string;
  url: string;
  author: string;
  createdAt: number;
  interactions: number;
}

/**
 * X/Twitter "$WAXP mentions" marquee — stacked directly above the sales
 * ticker (see .x-ticker in global.css, positioned bottom: var(--ticker-height)
 * so it sits right on top of .sales-ticker at bottom: 0). Rendered once in
 * App.tsx so it persists across navigation like the sales ticker.
 *
 * Backed by netlify/functions/x-mentions.ts, a server-side proxy for API
 * Direct's Twitter Posts API (https://apidirect.io) — see that file for
 * why this can't call it directly from the browser, and for the 18h
 * cache that keeps usage inside the free 50-calls/month plan. Until an
 * APIDIRECT_API_KEY is configured on the Netlify site, that function
 * returns an empty list, so this renders as a static "waiting for
 * mentions" bar rather than disappearing or erroring — same treatment as
 * an empty results page.
 */
export function XMentionsTicker() {
  const [mentions, setMentions] = useState<XMention[]>([]);
  const [, setTick] = useState(0); // keeps "X ago" labels fresh

  useEffect(() => {
    let cancelled = false;

    function load() {
      fetch("/.netlify/functions/x-mentions")
        .then((r) => (r.ok ? r.json() : { posts: [] }))
        .then((data) => {
          if (!cancelled) setMentions(Array.isArray(data.posts) ? data.posts : []);
        })
        .catch(() => {
          /* best-effort — keep showing the last good list */
        });
    }

    load();
    const refreshId = window.setInterval(load, REFRESH_MS);
    const tickId = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refreshId);
      window.clearInterval(tickId);
    };
  }, []);

  const hasMentions = mentions.length > 0;
  const duration = Math.max(20, Math.round(mentions.length * SECONDS_PER_ITEM));

  const row = (copyKey: string) => (
    <div className="x-ticker__row" key={copyKey}>
      {mentions.map((post) => (
        <a
          key={`${copyKey}-${post.id}`}
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          className="x-ticker__item"
          tabIndex={-1}
        >
          <span className="x-ticker__handle">@{post.author}</span>
          <span className="x-ticker__text">{post.text}</span>
          <span className="x-ticker__time">{formatRelativeTime(post.createdAt)}</span>
        </a>
      ))}
    </div>
  );

  return (
    <div className="x-ticker">
      <span className="x-ticker__badge">𝕏 $WAXP MENTIONS</span>
      <div className="x-ticker__viewport" aria-hidden="true">
        {hasMentions ? (
          <div className="x-ticker__track" style={{ animationDuration: `${duration}s` }}>
            {row("a")}
            {row("b")}
          </div>
        ) : (
          <div className="x-ticker__empty">Waiting for new $WAXP mentions on X…</div>
        )}
      </div>
    </div>
  );
}
